import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ResultType } from '../models/enums/result-type.enum';
import { CalculationResult } from '../Modules/informate/models/calculationModels/calculation-result';
import { InputGroup } from '../Modules/informate/models/calculationModels/input-group';
import { DiagramDimensionValidationModel } from '../Modules/informate/models/diagram-dimension-validation-model';
import { GasketDimensionValuesRtjModel } from '../Modules/informate/models/gasket-dimension-values-rtj-model';
import {
    AdditionalThicknessModel,
    BoltDimensionModel,
    BoltMaterialModel,
    ClampMaterialModel,
    ClampModel,
    DiagramModel,
    FilterData,
    FlangeDimensionModel,
    FlangeDimensionsByFlangeTypeModel,
    FlangeFacingDimensionsByFlangeFacingTypeModel,
    FlangeFacingTypeModel,
    FlangeMaterialModel,
    FlangeTypeModel,
    GasketDimensionModel,
    GasketModel,
    GasketTypeModel,
    HubMaterialModel,
    IceGridReq,
    ImageModel,
    JointConfigurationModel,
    LubricantModel,
    ManufacturerModel,
    NutDimensionModel,
    PipeSizeModel,
    PressureClassModel,
    RingNumberModel,
    ScheduleModel,
    SealRingMaterialModel,
    SealRingModel,
    StandardModel,
    WasherModel,
    TableRefrences,
    TableFields,
    UpdateTableReq,
    CheckForDelete,
    DeleteReq,
} from '../Modules/informate/models/index';
import { PressureTempRatingModel } from '../Modules/informate/models/pressure-temp-rating-model';
import { AllowedItems } from '../Modules/settings/models/informate/allowed-items';
import { GlobalEmittersService } from './global-emitters.service';
import { environment } from 'src/environments/environment';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { APIRes } from '../Modules/idms/models/commonAPI.model';
import { boltLoadGroup } from '../Modules/informate/models/boltLoadGroups';
import { EnvironmentService } from './environment.service';

@Injectable({
    providedIn: 'root',
})
export class IceapiService {
    instanceId: string;
    headers: HttpHeaders;
    private iceApi = environment.iceApi;
    private subVal = '';
    calculationRes = new Subject<boolean>();
    private logedInUserId = this.authService?.instance?.getActiveAccount()?.idTokenClaims?.oid;
    constructor(
        private http: HttpClient,
        private globalEmitterService: GlobalEmittersService,
        private authService: MsalService,
        private EnvironmentService: EnvironmentService,
        private appInsights: ApplicationInsights
    ) {
        //this.instanceId = "9c88866d-fc32-4da8-90e2-1b4e781418e3";
        this.subVal = this.EnvironmentService.getConfig().subValue;
        this.instanceId = `${localStorage.getItem('currentInstanceId')}`;
        this.headers = new HttpHeaders({
            'Ocp-Apim-Subscription-Key': this.subVal,
            Authorization: `Bearer ${localStorage.getItem('msal.accessToken')}`,
        });

        this.globalEmitterService.currentInstanceId.subscribe((res) => {
            if (res) {
                this.instanceId = res;
            }
        });
    }

    addPostAndPutHeaders(addSource: boolean = false) {
        this.headers = new HttpHeaders();
        this.headers = this.headers.append('Ocp-Apim-Subscription-Key', this.subVal);
        this.headers = this.headers.append('content-type', 'application/json');
        this.headers = this.headers.append('Accept', 'application/json');
        this.headers = this.headers.append('Access-Control-Allow-Origin', '*');
        this.headers = this.headers.append('Content-Type', 'multipart/form-data');
        this.headers = this.headers.append('Authorization', `Bearer ${localStorage.getItem('msal.accessToken')}`);
        if (addSource) {
            this.headers = this.headers.append('Source', '3');
        }
    }

    getLubricants(): Observable<APIRes<LubricantModel[]>> {
        this.addPostAndPutHeaders();
        return this.http
            .get(`${this.iceApi}lubricants`, {
                headers: this.headers,
                params: new HttpParams().set('instanceId', this.instanceId),
            })
            .pipe(map((res: LubricantModel[]) => res))
            .pipe(catchError(this.handleError.bind(this)));
    }

    getBoltDimensions(isMetric: boolean, ThreadSeries?: string): Observable<APIRes<BoltDimensionModel[]>> {
        let queryParams;
        if (isMetric != null) {
            queryParams = new HttpParams().set('IsMetric', isMetric.toString());
        }
        if (ThreadSeries) {
            queryParams = this.createHttpParams({ IsMetric: isMetric.toString(), ThreadSeries: ThreadSeries });
        }
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}BoltDimensions`, { headers: this.headers, params: queryParams }).pipe(catchError(this.handleError.bind(this)));
    }

    getNutDimensions(boltDimensionId: string): Observable<APIRes<NutDimensionModel[]>> {
        this.addPostAndPutHeaders();
        return this.http
            .get(`${this.iceApi}NutDimensions`, { headers: this.headers, params: new HttpParams().set('boltDimensionId', boltDimensionId) })
            .pipe(catchError(this.handleError.bind(this)));
    }

    getStandards(): Observable<APIRes<StandardModel[]>> {
        this.addPostAndPutHeaders();
        return this.http
            .get(`${this.iceApi}Standards`, { headers: this.headers, params: new HttpParams().set('instanceId', this.instanceId) })
            .pipe(catchError(this.handleError.bind(this)));
    }

    getAlloweditems(instanceId: any): Observable<APIRes<AllowedItems>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}alloweditems/` + instanceId, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    getDiagrams(): Observable<APIRes<DiagramModel[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}diagrams`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    getDiagram(diagramId: string, diagramName: string): Observable<APIRes<DiagramModel>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ diagramId: diagramId, name: diagramName });

        let url = this.iceApi + 'diagrams';
        return this.http.get(url, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getDiagramDimensionValidations(diagramId: string, diagramType: string): Observable<APIRes<DiagramDimensionValidationModel[]>> {
        this.addPostAndPutHeaders();
        let params: HttpParams;
        if (diagramId === null) {
            params = this.createHttpParams({ diagramType: diagramType });
        } else {
            params = this.createHttpParams({ diagramId: diagramId, diagramType: diagramType });
        }

        return this.http
            .get(`${this.iceApi}diagramDimensionValidations`, { headers: this.headers, params: params })
            .pipe(catchError(this.handleError.bind(this)));
    }

    getImages(): Observable<APIRes<ImageModel[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}images`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    getRingNumbers(): Observable<APIRes<RingNumberModel[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}RingNumbers`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    getPressureClasses(standardId: string): Observable<APIRes<PressureClassModel[]>> {
        this.addPostAndPutHeaders();
        return this.http
            .get(`${this.iceApi}PressureClasses`, { headers: this.headers, params: new HttpParams().set('standardId', standardId) })
            .pipe(catchError(this.handleError.bind(this)));
    }

    getPipeSizes(
        clampId: string,
        pressureClassId: string,
        minimumPipeSize?: number,
        maximumPipeSize?: number,
        IsMetric?: boolean
    ): Observable<APIRes<PipeSizeModel[]>> {
        this.addPostAndPutHeaders();
        let isMatric = IsMetric?.toString();
        const params: HttpParams = this.createHttpParams({
            clampId: clampId,
            pressureClassId: pressureClassId,
            minimumPipeSize: minimumPipeSize,
            maximumPipeSize: maximumPipeSize,
            isMetric: isMatric,
        });

        return this.http.get(`${this.iceApi}PipeSizes`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getFlangeTypes(standardId: string, pressureClassId: string, pipeSizeId: string, isCustom?: boolean): Observable<APIRes<FlangeTypeModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({
            standardId: standardId,
            PressureClassValueId: pressureClassId,
            pipeSizeId: pipeSizeId,
            isCustom: isCustom,
        });

        return this.http.get(`${this.iceApi}FlangeTypes`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getFlangeFacingTypes(flangeTypeId: string, pressureClassId: string, pipeSizeId?: string): Observable<APIRes<FlangeFacingTypeModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ flangeTypeId: flangeTypeId, pressureClassValueId: pressureClassId, pipeSizeId: pipeSizeId });

        return this.http.get(`${this.iceApi}FlangeFacingTypes`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getFlangeMaterials(standardId?: string, isCustom?: boolean): Observable<APIRes<FlangeMaterialModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ instanceId: this.instanceId, standardId: standardId, isCustom: isCustom });

        return this.http.get(`${this.iceApi}FlangeMaterials`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getGaskets(
        standardId: string,
        flangeFacingTypeId: string,
        pressureClassId: string,
        pipeSizeId: string,
        isStandardFlange: boolean,
        isRTJ: boolean,
        isCustom: boolean
    ): Observable<APIRes<GasketModel[]>> {
        const params: HttpParams = this.createHttpParams({
            instanceId: this.instanceId,
            standardId: standardId,
            flangeFacingTypeId: flangeFacingTypeId,
            pressureClassId: pressureClassId,
            pipeSizeId: pipeSizeId,
            isStandardFlange: isStandardFlange?.toString(),
            isRTJ: isRTJ?.toString(),
            isCustom: isCustom?.toString(),
        });

        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}Gaskets`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getGasketTypesForIdmsSettings(): Observable<APIRes<GasketModel[]>> {
        const params: HttpParams = this.createHttpParams({ instanceId: this.instanceId });
        this.addPostAndPutHeaders();

        return this.http.get(`${this.iceApi}Gaskets`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getGasketTypes(
        standardId: string,
        pressureClassId: string,
        flangeFacingTypeId: string,
        pipeSizeId: string,
        isRtj: boolean | null,
        calculationType?: number
    ): Observable<APIRes<GasketTypeModel[]>> {
        const params = this.createHttpParams({
            instanceId: this.instanceId,
            standardId,
            pressureClassId,
            flangeFacingTypeId,
            pipeSizeId,
            isRTJ: isRtj !== null && isRtj !== undefined ? isRtj.toString() : undefined,
            calculationType: calculationType !== null && calculationType !== undefined ? calculationType.toString() : undefined,
        });

        this.addPostAndPutHeaders();

        return this.http
            .get<APIRes<GasketTypeModel[]>>(`${this.iceApi}GasketTypes`, {
                headers: this.headers,
                params,
            })
            .pipe(catchError(this.handleError.bind(this)));
    }

    getGasketTypesForMaterialAlias(): Observable<APIRes<GasketTypeModel[]>> {
        let params: HttpParams = this.createHttpParams({ instanceId: this.instanceId });
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}GasketTypes`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getGasketDimension(gasketTypeId: string, pressureClassId: string, pipeSizeId: string): Observable<APIRes<GasketDimensionModel>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ gasketTypeId: gasketTypeId, pressureClassValueId: pressureClassId, pipeSizeId: pipeSizeId });

        return this.http
            .get(`${this.iceApi}GasketDimensions`, {
                headers: this.headers,
                params: params,
            })
            .pipe(catchError(this.handleError.bind(this)));
    }

    getGasketDimensionValuesRtj(ringNumberId: string): Observable<APIRes<GasketDimensionValuesRtjModel>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ ringNumberId: ringNumberId });

        return this.http.get(`${this.iceApi}GasketDimensionValuesRTJ`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getBoltMaterials(standardId?: string, isCustom?: boolean): Observable<APIRes<BoltMaterialModel[]>> {
        const params: HttpParams = this.createHttpParams({ instanceId: this.instanceId, standardId: standardId, isCustom: isCustom });
        this.addPostAndPutHeaders();

        return this.http.get(`${this.iceApi}BoltMaterials`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getWasherDimensions(boltType: string, pressureClassId: string, pipeSizeId: string, boltSize: number): Observable<APIRes<WasherModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ boltType: boltType, pressureClassId: pressureClassId, pipeSizeId: pipeSizeId, boltSize: boltSize });

        return this.http.get(`${this.iceApi}WasherDimensions`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getPipeSchedules(pipeSizeId: string, clampId: string): Observable<{ success?: boolean; isCustom?: boolean; message?: string; response?: ScheduleModel[] }> {
        const params: HttpParams = this.createHttpParams({ pipeSizeId: pipeSizeId, clampId: clampId });
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}PipeSchedules`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getAdditionalThicknesses(pressureClassId: string, pipeSizeId: string): Observable<APIRes<AdditionalThicknessModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ pressureClassValueId: pressureClassId, pipeSizeId: pipeSizeId });
        return this.http.get(`${this.iceApi}AdditionalThicknesses`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getFlangeDimensions(pressureClassId: string, pipeSizeId: string): Observable<APIRes<FlangeDimensionModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ pressureClassId: pressureClassId, pipeSizeId: pipeSizeId });

        return this.http.get(`${this.iceApi}FlangeDimensions`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getFlangeDimensionsByFlangeType(flangeTypeId: string, flangeDimensionId: string): Observable<APIRes<FlangeDimensionsByFlangeTypeModel>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ flangeTypeId: flangeTypeId, flangeDimensionId: flangeDimensionId });

        return this.http
            .get(`${this.iceApi}FlangeDimensionsByFlangeTypes`, { headers: this.headers, params: params })
            .pipe(catchError(this.handleError.bind(this)));
    }

    getFlangeFacingDimensionsByFlangeFacingType(
        flangeFacingTypeId: string,
        pressureClassId: string,
        pipeSizeId: string
    ): Observable<APIRes<FlangeFacingDimensionsByFlangeFacingTypeModel>> {
        const params: HttpParams = this.createHttpParams({ flangeFacingTypeId: flangeFacingTypeId, pressureClassId: pressureClassId, pipeSizeId: pipeSizeId });
        this.addPostAndPutHeaders();
        return this.http
            .get(`${this.iceApi}FlangeFacingDimensionsByFlangeFacingTypes`, { headers: this.headers, params: params })
            .pipe(catchError(this.handleError.bind(this)));
    }

    getManufacturers(manufacturerType: number, minimumNominalPipeSize?: number, maximumNominalPipeSize?: number): Observable<APIRes<ManufacturerModel[]>> {
        const params: HttpParams = this.createHttpParams({
            instanceId: this.instanceId,
            manufacturerType: manufacturerType,
            minimumNominalSize: minimumNominalPipeSize,
            maximumNominalSize: maximumNominalPipeSize,
        });
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}Manufacturers`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getClamp(clampId: string): Observable<APIRes<ClampModel>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}clamps/${clampId}`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    getClamps(manufacturerId: string): Observable<APIRes<ClampModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ manufacturerId: manufacturerId });

        return this.http.get(`${this.iceApi}clamps`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getClampMaterials(manufacturerId: string): Observable<APIRes<ClampMaterialModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ manufacturerId: manufacturerId });

        return this.http.get(`${this.iceApi}clampmaterials`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getHubMaterials(clampMaterialId: string): Observable<APIRes<HubMaterialModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ clampMaterialId: clampMaterialId });

        return this.http.get(`${this.iceApi}hubmaterials`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getSealRingMaterials(manufacturerId: string, hubMaterialId: string): Observable<APIRes<SealRingMaterialModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ manufacturerId: manufacturerId, hubMaterialId: hubMaterialId });

        return this.http.get(`${this.iceApi}sealringmaterials`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getSealRings(pipeSizeId: string, pipeScheduleId: string, clampId: string): Observable<APIRes<SealRingModel[]>> {
        this.addPostAndPutHeaders();
        const params: HttpParams = this.createHttpParams({ pipeSizeId: pipeSizeId, pipeScheduleId: pipeScheduleId, clampId: clampId });

        return this.http.get(`${this.iceApi}sealrings`, { headers: this.headers, params: params }).pipe(catchError(this.handleError.bind(this)));
    }

    getJointConfigurations(): Observable<APIRes<JointConfigurationModel[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}JointConfigurations`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    postCalculateStandardFlange(inputGroup: InputGroup, addSource: boolean = false): Observable<APIRes<CalculationResult>> {
        this.addPostAndPutHeaders(addSource);

        let inputMsg = JSON.stringify(inputGroup);
        return this.http
            .post(this.iceApi + 'calculations/flange' + '?instanceId=' + this.instanceId, inputMsg, { headers: this.headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    postCalculateEngineeredJoint(inputGroup: InputGroup, addSource: boolean = false): Observable<APIRes<CalculationResult>> {
        this.addPostAndPutHeaders(addSource);

        let inputMsg = JSON.stringify(inputGroup);
        return this.http
            .post(this.iceApi + 'calculations/EngineeredJoint' + '?instanceId=' + this.instanceId, inputMsg, { headers: this.headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    postCalculateClamp(inputGroup: InputGroup, addSource: boolean = false): Observable<APIRes<CalculationResult>> {
        this.addPostAndPutHeaders(addSource);
        let inputMsg = JSON.stringify(inputGroup);
        return this.http
            .post(this.iceApi + 'calculations/clamp' + '?instanceId=' + this.instanceId, inputMsg, { headers: this.headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    postAlloweditems(allowedItem: AllowedItems, instanceId: string): Observable<ResultType> {
        this.addPostAndPutHeaders();

        let inputMsg = JSON.stringify(allowedItem);
        return this.http
            .post(this.iceApi + 'alloweditems/' + instanceId, inputMsg, { headers: this.headers, observe: 'response' })
            .pipe(
                map((response) => {
                    return this.extractResultType(response.status);
                })
            )
            .pipe(catchError(this.handleError.bind(this)));
    }

    private handleError(error: HttpErrorResponse) {
        this.logedInUserId = this.authService?.instance?.getActiveAccount()?.idTokenClaims?.oid;
        let currOrg = localStorage.getItem('selectedOrgId');
        let currInstance = localStorage.getItem('currentInstanceId');
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error.message);

            this.appInsights.trackException({
                exception: new Error(error.message),
                properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId },
            });
            //this.appInsights.trackPageView({ properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId } });
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong.
            console.error(`Backend returned code ${error.status}, ` + `body was: ${error.message}`);
            this.appInsights.trackException({
                exception: new Error(error.message),
                properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId },
            });
            //this.appInsights.trackPageView({ properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId } });
        }
        // Return an observable with a user-facing error message.
        this.appInsights.trackException({
            exception: new Error('Something bad happened; please try again later.'),
            properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId },
        });
        //this.appInsights.trackPageView({ properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId } });
        return throwError('Something bad happened; please try again later.');
    }

    private createHttpParams(params: {}): HttpParams {
        let httpParams: HttpParams = new HttpParams();
        Object.keys(params).forEach((param) => {
            if (params[param]) {
                httpParams = httpParams.set(param, params[param]);
            }
        });

        return httpParams;
    }

    getPressureTempRatings(body: any): Observable<APIRes<PressureTempRatingModel[]>> {
        this.addPostAndPutHeaders();
        return this.http.post(`${this.iceApi}PressureTempRatings`, body, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    private extractResultType(statusCode: number): ResultType {
        if (statusCode >= 200 || statusCode < 300) {
            return ResultType.Success;
        } else {
            return ResultType.Error;
        }
    }

    getBoltDimensionsByBoltSize(isMetric: boolean, ThreadSeries?: string, BoltSizeName?: string): Observable<APIRes<BoltDimensionModel>> {
        // const params: HttpParams = this.createHttpParams({ IsMetric: isMetric })
        let queryParams;
        if (isMetric != null) {
            queryParams = new HttpParams().set('IsMetric', isMetric.toString());
        }
        if (ThreadSeries) {
            queryParams = this.createHttpParams({ IsMetric: isMetric.toString(), ThreadSeries: ThreadSeries, BoltSizeName: BoltSizeName });
        }

        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}BoltDimensions`, { headers: this.headers, params: queryParams }).pipe(catchError(this.handleError.bind(this)));
    }

    getBoltTypes(boltMinDiameter: number, boltMaxDiameter: number): Observable<APIRes<number[]>> {
        // const params: HttpParams = this.createHttpParams({ IsMetric: isMetric })
        let queryParams;

        if (boltMaxDiameter) {
            queryParams = this.createHttpParams({ boltMinDiameter: boltMinDiameter, boltMaxDiameter: boltMaxDiameter });
        }
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}bolttypes`, { headers: this.headers, params: queryParams }).pipe(catchError(this.handleError.bind(this)));
    }

    // ***************API Call to get Table Refrences ************************** */
    getIceDatabaseRefrences(): Observable<APIRes<TableRefrences[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}database/GetTableRefrences`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // ***************API Call to get data for the Selected Table ************************** */
    getIceDatabaseGridData(payload: IceGridReq): Observable<APIRes<any>> {
        this.addPostAndPutHeaders();
        return this.http.post(`${this.iceApi}database/GetGridData`, payload, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // ***************API Call to get filterList for Selected Table ************************** */
    getFilterData(tableName: string): Observable<APIRes<FilterData[]>> {
        this.addPostAndPutHeaders();
        return this.http
            .get(`${this.iceApi}database/GetFilterData`, { headers: this.headers, params: new HttpParams().set('tableName', tableName) })
            .pipe(catchError(this.handleError.bind(this)));
    }

    // ***************API Call to get Table Fields ************************** */
    getTableFields(tableName: string): Observable<APIRes<TableFields[]>> {
        this.addPostAndPutHeaders();
        return this.http
            .get(`${this.iceApi}database/GetTableFields`, { headers: this.headers, params: new HttpParams().set('tableName', tableName) })
            .pipe(catchError(this.handleError.bind(this)));
    }

    // ***************API Call to update a TableData  ************************** */
    UpdateIceDatabaseTable(payload: UpdateTableReq): Observable<APIRes<any>> {
        this.addPostAndPutHeaders();
        return this.http.post(`${this.iceApi}database/UpdateTable`, payload, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // ***************API Call to check for Delete ************************** */
    CheckForDelete(payload: DeleteReq): Observable<APIRes<CheckForDelete[]>> {
        this.addPostAndPutHeaders();
        return this.http.post(`${this.iceApi}database/CheckForDelete`, payload, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // ***************API Call to Insert a TableData ************************** */
    InsertIceDatabaseTable(payload: UpdateTableReq): Observable<APIRes<any>> {
        this.addPostAndPutHeaders();
        return this.http.post(`${this.iceApi}database/InsertTableData`, payload, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // ***************API Call to get Bolt load Groups************************* */
    getBoltLoadGroups(instanceId: string, CalculationType: number): Observable<APIRes<boltLoadGroup[]>> {
        // const params: HttpParams = this.createHttpParams({ IsMetric: isMetric })
        let queryParams;

        queryParams = this.createHttpParams({ InstanceId: instanceId, CalculationType: CalculationType });

        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}boltloadgroups`, { headers: this.headers, params: queryParams }).pipe(catchError(this.handleError.bind(this)));
    }

    getwarningsbyType(warningType): Observable<APIRes<any>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.iceApi}warnings/${warningType}`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }
}
